import React from "react";
import { Tooltip } from "@mui/material";
import { IndicatorTone } from "../../../../utils/waterCare";

// Tone of each of the five indicator positions, low to high.
const TONES: IndicatorTone[] = ['bad', 'caution', 'good', 'caution', 'bad'];

interface LevelScaleProps {
    label: string;
    /** Indicator index 0–4 from the spa (sbORPind / sbpHind). */
    value: unknown;
    /** Published range per index; the current one is shown over the marker. */
    ranges: string[];
    unit?: string;
    /** Raw sensor reading, shown on hover of the row (e.g. "650 mV"). */
    detail?: string;
}

// One reading as a five-zone scale (red · amber · green · amber · red), read like
// a gauge: every zone is always visible but soft, the current zone is solid, and
// a marker above it names the range. Nothing here is clickable.
export const LevelScale = ({ label, value, ranges, unit, detail }: LevelScaleProps) => {
    const index = typeof value === 'number' ? Math.max(0, Math.min(4, value)) : null;
    const current = index !== null ? `${ranges[index]}${unit ? ` ${unit}` : ''}` : null;
    // The end markers align to the bar's edge so their text doesn't overflow it.
    const align = index === 0 ? 'start' : index === 4 ? 'end' : 'center';

    return (
        <Tooltip title={detail ?? ''} placement="top" arrow enterTouchDelay={0} disableHoverListener={!detail}>
            <div
                className="ls-row"
                role="meter"
                aria-label={label}
                aria-valuemin={0}
                aria-valuemax={4}
                aria-valuenow={index ?? undefined}
                aria-valuetext={current ?? 'No reading'}
            >
                <span className="ls-label">{label}</span>
                <div className="ls-scale">
                    <div className="ls-markers">
                        {index !== null && (
                            <span className={`ls-marker ${align}`} style={{ gridColumn: index + 1 }}>
                                <span className="ls-marker-text">{current}</span>
                                <span className="ls-marker-arrow" />
                            </span>
                        )}
                    </div>
                    <div className="ls-bar">
                        {TONES.map((tone, i) => (
                            <span key={i} className={`ls-seg ${tone} ${index === i ? 'current' : ''}`} />
                        ))}
                    </div>
                    <div className="ls-captions" aria-hidden="true">
                        <span>Low</span>
                        <span>OK</span>
                        <span>High</span>
                    </div>
                </div>
            </div>
        </Tooltip>
    );
};
